import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { MenuItem } from 'primeng/api/menuitem';
import { concat, Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { apiConfig } from '../../b2c-config';
import { MemberModel } from '../../models/member.model';
import { AccessRolesService } from '../../services/access-roles.service';
import { CommonService } from 'src/app/modules/shared/services/common.service';

@Component({
    selector: 'login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
    public isLoggedIn: boolean = false;
    public items: MenuItem[] = [];
    public userFullName: string|undefined = '';

    private _inactive: boolean = false;
    private _unsubscribe$: Subject<void> = new Subject<void>();

    @Output()
    public adminAccessChanged: EventEmitter<boolean> = new EventEmitter();

    @Output()
    public commonAccessChanged: EventEmitter<boolean> = new EventEmitter();

    @Input()
    public get inactive(): boolean {
        return this._inactive;
    }

    public set inactive(inactive: boolean) {
        this._inactive = inactive;
        if (inactive) {
            this.logout();
        }
    }

    @Output()
    public loginStatusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    public constructor(
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private broadcastService: MsalBroadcastService,
        private authService: MsalService,
        private appAuthService: AccessRolesService,
        private commonService: CommonService) { }

    public ngOnDestroy(): void {
        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    public ngOnInit(): void {
        this.items = [
            {
                label: 'Logout',
                icon: 'pi pi-fw pi-power-off',
                command: /* istanbul ignore next */(): void => this.logout()
            }
        ];

        this.broadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._unsubscribe$)
            )
            .subscribe(() => {
                this.setAndNotifyLoginStatus();
            });

        this.broadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.Logout),
                takeUntil(this._unsubscribe$)
            )
            .subscribe(() => {
                this.authService.instance.setActiveAccount(null);
            });

        this.getAuthenticationResult()
            .pipe(
                filter((result: AuthenticationResult) => result?.account !== null && result?.account !== undefined),
                mergeMap((result: AuthenticationResult) => {
                    const accountInfo = result.account!;
                    this.authService.instance.setActiveAccount(accountInfo);
                    this.setLoggedInUsername(accountInfo.name!);
                    return this.appAuthService.getRole();
                }),
                map(res => new MemberModel(res.member)),
                takeUntil(this._unsubscribe$))
            .subscribe(
                (result: MemberModel) => {
                    this.adminAccessChanged.emit(result.hasAdminAccess());
                    this.commonAccessChanged.emit(result.hasCommonAccess());
                    this.commonService.hasAdminAccess = result.hasAdminAccess();
                    this.commonService.hasCommonAccess = result.hasCommonAccess();
                },
                () => {
                    this.adminAccessChanged.emit(false);
                    this.commonAccessChanged.emit(false);
                    this.commonService.hasAdminAccess = false;
                    this.commonService.hasCommonAccess = false;
                    // this.onLoginClicked(); /* TODO: need a better way to handle this. display dialog? */
                }
            );
    }

    private getAuthenticationResult(): Observable<AuthenticationResult> {
        const redirectObs = this.authService.handleRedirectObservable();

        if (this.authService.instance.getActiveAccount()) {
            const request = {
                scopes: apiConfig.scopes
            };
            const silentTokenObs = this.authService.acquireTokenSilent(request);
            return concat(redirectObs, silentTokenObs);
        }
        return redirectObs;
    }

    private setLoggedInUsername(username: string): void {
        this.userFullName = username;
    }

    public onLoginClicked(): void {
        if (this.msalGuardConfig.authRequest) {
            sessionStorage.setItem('success', 'true'); // change text on logout and login
            this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
        } else {
            this.authService.loginRedirect();
        }
    }

    private setAndNotifyLoginStatus(): void {
        this.isLoggedIn = this.authService.instance.getAllAccounts().length > 0;
        this.loginStatusChanged.emit(this.isLoggedIn);
    }

    private logout(): void {
        this.authService.logoutRedirect({
            postLogoutRedirectUri: environment.redirectURL
        });
        sessionStorage.clear();
    }
}
